
import { logInfo, logDebug } from '@/utils/logging';

/**
 * Cache for books data to reduce database calls
 */
const BOOKS_CACHE = new Map<string, any>();
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_CACHE_SIZE = 100; // Maximum number of items to keep in cache

// Track when items were last accessed for LRU eviction
const CACHE_ACCESS_TIMESTAMPS = new Map<string, number>();

/**
 * Get cached book data if available
 * @param bookId The ID of the book to retrieve
 * @returns The cached book data or undefined if not in cache
 */
export const getCachedBook = (bookId: string) => {
  const cachedData = BOOKS_CACHE.get(bookId);
  
  if (cachedData) {
    const now = Date.now();
    
    // Check if cache has expired
    if ((now - cachedData.timestamp) < CACHE_EXPIRY_TIME) {
      // Update last access time for LRU algorithm
      CACHE_ACCESS_TIMESTAMPS.set(bookId, now);
      logDebug('Retrieved book from cache');
      return cachedData.data;
    } else {
      // Cache expired, remove it
      BOOKS_CACHE.delete(bookId);
      CACHE_ACCESS_TIMESTAMPS.delete(bookId);
      logDebug('Expired cache entry removed');
    }
  }
  
  return undefined;
};

/**
 * Cache book data
 * @param bookId The ID of the book to cache
 * @param data The book data to cache
 */
export const cacheBook = (bookId: string, data: any) => {
  logDebug('Caching book data');
  
  // Check if we need to evict items due to cache size
  if (BOOKS_CACHE.size >= MAX_CACHE_SIZE) {
    evictLeastRecentlyUsed();
  }
  
  const now = Date.now();
  BOOKS_CACHE.set(bookId, {
    data,
    timestamp: now
  });
  CACHE_ACCESS_TIMESTAMPS.set(bookId, now);
  
  // Schedule cleanup of expired items
  scheduleExpiredItemsCleanup();
};

/**
 * Clear all book caches
 */
export const clearBooksCache = () => {
  logInfo('Clearing all book caches');
  BOOKS_CACHE.clear();
  CACHE_ACCESS_TIMESTAMPS.clear();
};

/**
 * Clear cache for a specific book
 * @param bookId The ID of the book to clear from cache
 */
export const clearBookCache = (bookId: string) => {
  logDebug('Clearing cache for specific book');
  BOOKS_CACHE.delete(bookId);
  CACHE_ACCESS_TIMESTAMPS.delete(bookId);
};

/**
 * Evict least recently used items when cache is full
 */
function evictLeastRecentlyUsed() {
  if (CACHE_ACCESS_TIMESTAMPS.size === 0) return;
  
  // Find the least recently accessed item
  let oldestAccess = Date.now();
  let oldestKey = null;
  
  CACHE_ACCESS_TIMESTAMPS.forEach((timestamp, key) => {
    if (timestamp < oldestAccess) {
      oldestAccess = timestamp;
      oldestKey = key;
    }
  });
  
  if (oldestKey) {
    logDebug('Evicting least recently used cache item', { key: oldestKey });
    BOOKS_CACHE.delete(oldestKey);
    CACHE_ACCESS_TIMESTAMPS.delete(oldestKey);
  }
}

// Track if cleanup is already scheduled
let cleanupScheduled = false;

/**
 * Schedule cleanup of expired items
 */
function scheduleExpiredItemsCleanup() {
  if (cleanupScheduled) return;
  
  cleanupScheduled = true;
  
  // Schedule cleanup for next tick
  setTimeout(() => {
    cleanupExpiredItems();
    cleanupScheduled = false;
  }, 0);
}

/**
 * Remove all expired items from cache
 */
function cleanupExpiredItems() {
  const now = Date.now();
  const expiredKeys: string[] = [];
  
  BOOKS_CACHE.forEach((value, key) => {
    if ((now - value.timestamp) >= CACHE_EXPIRY_TIME) {
      expiredKeys.push(key);
    }
  });
  
  if (expiredKeys.length > 0) {
    logDebug('Cleaning up expired cache items', { count: expiredKeys.length });
    
    expiredKeys.forEach(key => {
      BOOKS_CACHE.delete(key);
      CACHE_ACCESS_TIMESTAMPS.delete(key);
    });
  }
}

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  return {
    size: BOOKS_CACHE.size,
    maxSize: MAX_CACHE_SIZE,
    expiryTimeMs: CACHE_EXPIRY_TIME
  };
};
